// The single navigation catalog shared by every Orion surface. Both the agent-driven `ClientControl`
// tool and a widget's `on_click: redirect` describe WHERE to go with the same `NavDestination`, and
// `resolveNavLink` turns that into the per-surface targets each surface knows how to open. This is the
// one place that knows web routes and mobile deep-links, so the two never drift.

/** A built-in app section. The user's own Orion pages are addressed by slug instead (see NavDestination). */
export let NavSection = /*#__PURE__*/function (NavSection) {
  NavSection["Home"] = "home";
  NavSection["Tasks"] = "tasks";
  NavSection["Pulses"] = "pulses";
  NavSection["Knowledge"] = "knowledge";
  NavSection["Connectors"] = "connectors";
  NavSection["Notifications"] = "notifications";
  NavSection["Agents"] = "agents";
  NavSection["Settings"] = "settings";
  NavSection["PhoneVerification"] = "phone-verification";
  return NavSection;
}({});

/**
 * Where navigation can go: a built-in section, one of the user's Orion pages by slug, or a whole
 * dashboard by slug (opened at its default page — e.g. a freshly-built workspace or catch-up
 * dashboard). `page` drills into ONE page of the home/catch-up dashboard; `dashboard` opens the
 * dashboard identified by that slug.
 */

/**
 * Per-surface navigation targets for one destination. `desktop` is a web route path relative to the
 * brand basePath (the web client prepends basePath + the conversation query); `mobile` is a full
 * `orion://` deep-link URL the mobile app's deep-link router resolves (see the app's deeplinks table). Either may
 * be absent — a surface with no target for a destination just no-ops (graceful degradation), so a
 * future destination can cover only one surface. Keeping `mobile` a deep-link (not a token the app
 * switches on) means a new destination is a new entry here plus a matching deep-link route — no central
 * switch to touch.
 */

export function isNavSection(value) {
  return typeof value === 'string' && Object.values(NavSection).includes(value);
}

// Mobile deep-links are full `orion://` URLs (the app's scheme; universal-link form is
// https://apps.nownia.com/<path>). The app's deep-link router resolves the `time/...` path.
const MOBILE_DEEP_LINK_PREFIX = 'orion://time';
const SECTION_LINKS = {
  [NavSection.Home]: {
    desktop: '/',
    mobile: `${MOBILE_DEEP_LINK_PREFIX}/home`
  },
  [NavSection.Tasks]: {
    desktop: '/tasks',
    mobile: `${MOBILE_DEEP_LINK_PREFIX}/tasks`
  },
  [NavSection.Pulses]: {
    desktop: '/pulses',
    mobile: `${MOBILE_DEEP_LINK_PREFIX}/pulses`
  },
  [NavSection.Knowledge]: {
    desktop: '/knowledge',
    mobile: `${MOBILE_DEEP_LINK_PREFIX}/knowledge`
  },
  [NavSection.Connectors]: {
    desktop: '/connectors',
    mobile: `${MOBILE_DEEP_LINK_PREFIX}/connectors`
  },
  [NavSection.Notifications]: {
    desktop: '/notifications',
    mobile: `${MOBILE_DEEP_LINK_PREFIX}/notifications`
  },
  [NavSection.Agents]: {
    desktop: '/agents',
    mobile: `${MOBILE_DEEP_LINK_PREFIX}/agents`
  },
  [NavSection.Settings]: {
    desktop: '/settings',
    mobile: `${MOBILE_DEEP_LINK_PREFIX}/settings`
  },
  // Take the user to deal with their phone number, resolved per surface. On web it's a step ON the home
  // route (the URL carries it so the conversation stays open behind it and a refresh mid-flow returns to
  // the same step). On the native apps it opens the independent Manage-phone screen (enter → verify),
  // reachable from within a conversation — the universal-link form so the client's deep-link router opens
  // it directly. NB this is a top-level route, not a `time/<section>` one, so it does not use the prefix.
  [NavSection.PhoneVerification]: {
    desktop: '/?verify=phone',
    mobile: 'https://symphony.wix.app/managePhone'
  }
};

/**
 * Resolve a destination to its per-surface targets. A built-in section maps through the static catalog;
 * a user page maps to the `/pages/<slug>` web route and the `orion://time/page/<slug>` mobile deep-link. Both
 * fields are present only where that surface can actually go there.
 */
export function resolveNavLink(destination) {
  if ('page' in destination) {
    const slug = destination.page;
    return {
      desktop: `/pages/${slug}`,
      mobile: `${MOBILE_DEEP_LINK_PREFIX}/page/${slug}`
    };
  }
  if ('dashboard' in destination) {
    // A whole dashboard by slug, opened at its default page. Mobile deep-links to
    // `orion://time/dashboard/<slug>` (the app opens the dashboard's default page); web renders the
    // same dashboard through the `/pages/<slug>` route (its dashboard render resolves the slug).
    const slug = destination.dashboard;
    return {
      desktop: `/pages/${slug}`,
      mobile: `${MOBILE_DEEP_LINK_PREFIX}/dashboard/${slug}`
    };
  }
  return SECTION_LINKS[destination.section];
}

/** Parse an untrusted value into a NavDestination, or null when it isn't one. */
export function normalizeNavDestination(raw) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const _ref = raw,
    section = _ref.section,
    page = _ref.page,
    dashboard = _ref.dashboard;
  if (typeof page === 'string' && page.length > 0) {
    return {
      page
    };
  }
  if (typeof dashboard === 'string' && dashboard.length > 0) {
    return {
      dashboard
    };
  }
  if (isNavSection(section)) {
    return {
      section
    };
  }
  return null;
}
//# sourceMappingURL=Navigation.js.map